import React, { useState } from 'react';
import {
  App,
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';

const ACCRUAL_METHODS = [
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Yearly', label: 'Yearly (upfront)' },
  { value: 'None', label: 'None (manual / opening only)' },
];

const LeaveTypesPage: React.FC = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form] = Form.useForm();

  const { data: rows = [], isLoading } = useQuery({
    queryKey: hrmsKeys.leaveTypes(),
    queryFn: () => hrmsService.listLeaveTypes(),
  });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      is_paid: true,
      is_accrued: true,
      accrual_method: 'Monthly',
      annual_entitlement: 30,
      max_carry_forward: 15,
      encashable: true,
      requires_approval: true,
      is_active: true,
      sort_order: 100,
    });
    setDrawerOpen(true);
  };

  const openEdit = (r: any) => {
    setEditing(r);
    form.setFieldsValue({
      leave_code: r.leave_code,
      leave_name: r.leave_name,
      is_paid: r.is_paid !== false,
      is_accrued: !!r.is_accrued,
      annual_entitlement: Number(r.annual_entitlement || 0),
      accrual_method: r.accrual_method || 'Monthly',
      max_carry_forward: Number(r.max_carry_forward || 0),
      encashable: !!r.encashable,
      requires_approval: r.requires_approval !== false,
      gender_restriction: r.gender_restriction || undefined,
      is_active: r.is_active !== false,
      sort_order: r.sort_order ?? 100,
    });
    setDrawerOpen(true);
  };

  const save = async () => {
    try {
      const v = await form.validateFields();
      if (editing) await hrmsService.updateLeaveType(editing.id, v);
      else await hrmsService.createLeaveType(v);
      message.success(editing ? 'Leave type updated' : 'Leave type created');
      setDrawerOpen(false);
      queryClient.invalidateQueries({ queryKey: hrmsKeys.leaveTypes() });
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Save failed');
    }
  };

  const remove = async (id: string) => {
    try {
      await hrmsService.deleteLeaveType(id);
      message.success('Leave type deleted');
      queryClient.invalidateQueries({ queryKey: hrmsKeys.leaveTypes() });
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Delete failed');
    }
  };

  const columns: ColumnsType<any> = [
    { title: 'Code', dataIndex: 'leave_code', width: 110 },
    { title: 'Name', dataIndex: 'leave_name' },
    {
      title: 'Paid',
      dataIndex: 'is_paid',
      width: 70,
      render: (v) => (v ? <Tag color="green">Paid</Tag> : <Tag>Unpaid</Tag>),
    },
    { title: 'Entitlement', dataIndex: 'annual_entitlement', width: 100 },
    { title: 'Accrual', dataIndex: 'accrual_method', width: 100 },
    { title: 'Max carry', dataIndex: 'max_carry_forward', width: 90 },
    {
      title: 'Encash',
      dataIndex: 'encashable',
      width: 70,
      render: (v) => (v ? 'Yes' : 'No'),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      width: 70,
      render: (v) => (v !== false ? <Tag color="blue">Active</Tag> : <Tag>Off</Tag>),
    },
    {
      title: '',
      width: 100,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Delete leave type?" onConfirm={() => remove(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Leave types"
          subtitle="Annual entitlement, accrual, carry-forward and encashment rules"
          primaryActions={
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add leave type
            </Button>
          }
        />
      }
    >
      <Table rowKey="id" loading={isLoading} dataSource={rows} columns={columns} pagination={false} />

      <Drawer
        title={editing ? 'Edit leave type' : 'New leave type'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={480}
        extra={
          <Button type="primary" onClick={save}>
            Save
          </Button>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="leave_code" label="Code" rules={[{ required: true }]}>
            <Input disabled={!!editing} placeholder="ANNUAL" />
          </Form.Item>
          <Form.Item name="leave_name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="Annual Leave" />
          </Form.Item>
          <Form.Item name="annual_entitlement" label="Annual entitlement (days)" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="accrual_method" label="Accrual method" rules={[{ required: true }]}>
            <Select options={ACCRUAL_METHODS} />
          </Form.Item>
          <Form.Item name="max_carry_forward" label="Max carry-forward days">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_paid" label="Paid leave" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="is_accrued" label="Accrue monthly/yearly" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="encashable" label="Encashable at year-end" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="requires_approval" label="Requires approval" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="gender_restriction" label="Gender restriction">
            <Select
              allowClear
              options={[
                { value: 'Male', label: 'Male only' },
                { value: 'Female', label: 'Female only' },
              ]}
            />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="sort_order" label="Sort order">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Drawer>
    </WorkspaceLayout>
  );
};

export default LeaveTypesPage;
